import newShop from '../newShop';
import CartItem from './cartItem.js';
import ShopContext from '../shopContext.js';
import {AccountManager, UserService} from '@nuskin/ns-account';
import {events, storage, NUSAnalytics, PersonalOfferStorageService} from '@nuskin/ns-util';
import webLoyalty from '@nuskin/ns-loyalty-web';
import {PriceType} from '@nuskin/ns-product-lib';
import CurrencyService from '../currencyService';
import ScanCard from '../adr/scanCard';
import SalesEventService from "../salesEventService";
import {AdrService} from "@nuskin/ns-shop";
import { getCachedConfigField } from '@nuskin/configuration-sdk';


let doneSyncingPersistedCart = false;
events.subscribe(events.shop.CART_SYNCED_ON_LOAD, (doneSyncing) => {
    doneSyncingPersistedCart = !!doneSyncing;
}, true);


/**
 *
 * Cart
 *
 * @namespace Cart
 */
export default function Cart(cartData) {
    let id = ''; // persistent cart ID
    let version = -1; // persistent cart version
    let cartInitializing = true;
    let itemTable = new ItemTable();
    let totalOrderPrice = 0.0;
    let totalAdrPrice = 0.0;
    let totalOrderPsv = 0.0;
    let totalAdrPsv = 0.0;
    let totalOrderCsv = 0.0;
    let totalAdrCsv = 0.0;

    let totalSapOrderPrice = 0.0;
    let totalSapAdrPrice = 0.0;
    let totalSapOrderPsv = 0.0;
    let totalSapAdrPsv = 0.0;
    let totalSapOrderCsv = 0.0;
    let totalSapAdrCsv = 0.0;

    let totalOrderPoints = 0.0;

    let loyaltyRewards = false;
    let loyaltyRewardPoints = 0.0;
    let loyaltyAdrRewards = false;
    let loyaltyAdrRewardPoints = 0.0;
    let loyaltyTotalEarnPrice = 0.0;
    let loyaltyTotalRedeemPrice = 0.0;
    let loyaltyTotalEarnAndRedeemPrice = 0.0;
    let loyaltyTotalAdrEarnAndRedeemPrice = 0.0;
    let loyaltyTotalRedeemPriceOrderItems = 0.0;
    let loyaltySapTotalEarnPrice = 0.0;
    let loyaltySapTotalRedeemPrice = 0.0;
    let loyaltySapTotalRedeemPriceOrderItems = 0.0;

    let cntryCd = '';
    let userId = '';
    let type = 'market';
    let scanCards = [];
    let signupInfo = {
        checkForSignup: true,
        inSignup: false
    };
    let timestamp = Date.now();
    let isPitchCart = false;
    let isGift = false;
    let summerPromo = false;

    let payForShippingWithPoints = false;
    let currencyCode = '';

    let scope = this;

    // ---------------------------------------------
    //
    //        Define all public methods.
    //
    // ---------------------------------------------

    /**
     * toJSON - returns the cart in JSON format
     * @memberof Cart
     * @example
     * Cart.toJSON()
     * @returns {{userId: string, cntryCd: string, summerPromo:boolean,
     * loyaltyRewards:boolean, loyaltyRewardPoints:number, loyaltyAdrRewards:boolean, loyaltyAdrRewardPoints:number,
     * payForShipingWithPoints:boolean, isPitchCart: boolean, timestamp: number, scanCards: Array, signupInfo: object, products: Array}}
     */
    this.toJSON = function(){
        let jsonData = {
            "id": id,
            "version": version,
            "userId": userId,
            "cntryCd": cntryCd,
            "type": type,
            "summerPromo": summerPromo,
            "payForShippingWithPoints": payForShippingWithPoints,
            "isGift": isGift,
            "isPitchCart": isPitchCart,
            "timestamp": timestamp,
            "scanCards": [],
            "signupInfo": signupInfo,
            "products" : [],
            "loyaltyRewards": loyaltyRewards,
            "loyaltyRewardPoints": loyaltyRewardPoints,
            "loyaltyAdrRewards": loyaltyAdrRewards,
            "loyaltyAdrRewardPoints": loyaltyAdrRewardPoints,
            "loyaltyTotalPoints" : loyaltyRewardPoints + loyaltyAdrRewardPoints
        };
        let cartItems = getAllItems({});

        for (let j = 0; j < scanCards.length; j++) {
            jsonData.scanCards.push(scanCards[j].toJSON());
        }

        for (let i=0;i<cartItems.length;i++){
            jsonData.products.push(cartItems[i].toJSON());
        }

        return jsonData;
    };

    /**
     * returns an array of json objects representing the cartItems currently
     *  in the cart. The data is flattened so it is only one level deep.
     *
     * @param options - which items to get. Default is cartItems.
     * @returns {Array}
     */
    this.getItemData = (options = {cartItems: true}) => {
        let retVal = [];

        this.getItems(options).forEach(cartItem => {
            retVal.push(cartItem.toFlatJson());
        });

        return retVal;
    };

    /**
     * addCartItem - adds a CartItem to the cart.
     * @memberof Cart
     * @example
     * Cart.addCartItem(cartItem, replace)
     * @param cartItem
     */
    this.addCartItem = function(cartItem, replace, referrer) {
        if (cartItem instanceof CartItem && cartItem.product) {
            cartItem.key = getItemKey(cartItem);
            const origItem = itemTable.get(cartItem.key);
            itemTable.add(cartItem, replace);
            if (cartItem.isSapLineItem()) {
                updateSapTotals();
            } else {
                updateCartTotals();
            }
            if (replace && origItem) {
                publish(events.shop.CART_UPDATED, {
                    modify: {
                        type: 'quantity-changed',
                        key: cartItem.key,
                        item: cartItem.toFlatJson(),
                        qtyDiff: cartItem.qty - origItem.qty
                    }
                }, referrer);
            } else {
                publish(events.shop.CART_UPDATED, {add: {item: cartItem.toFlatJson()}}, referrer);
            }
        }
    };

    /**
     * addProduct - Adds a product to the cart in a CartItem object.  If the product
     *   already exists in the cart it will increment the quantity. Products can be
     *   added as ADR, one-time, business portfolio.  SAP items can also be added divided
     *   by ADR and one-time items.  SAP items have a sapLineId.
     * @memberof Cart
     * @example
     * Cart.addProduct(info)
     * @param info  contains info about the product including:
     *                  product
     *                  qty
     *                  maxQty
     *                  adr
     *                  isBusinessPortfolio
     *                  sapLineId
     *                  canDelete - optional, defaults to true
     *                  referrer - optional, contains the referrer that called this function, or in other words, where the product
     *                              was added from (search, productPage, categoryPage, etc.)
     */
    this.addProduct = function(info){
        let referrer = "unknown";
        if (typeof (info.referrer) === 'string' && info.referrer.length > 1) {
            referrer = info.referrer;
        }
        let intQuantity = parseInt(info.qty);
        let boolAdr = Boolean(info.adr);
        let boolBp = Boolean(info.isBusinessPortfolio);
        let sapLineId = info.sapLineId || null;
        //        var currentItem = this.getItemBySku(info.product.getSku(), boolAdr, boolBp, null, info.product.getAgelocmeKeyPart());
        let currentItem = this.getItemByKey(getItemKey(info.product.sku, boolAdr, boolBp, sapLineId, info.product.getAgelocmeKeyPart()));
        if (currentItem) {
            if (!boolBp) {
                let currentQty = currentItem.getQty();
                if (!currentQty || isNaN(currentQty)) {
                    currentQty = 0;
                }
                if (info.maxQty <= 0) {
                    // this means there is no max qty
                    currentQty = currentQty + intQuantity;
                } else {
                    currentQty = (currentQty + intQuantity > info.maxQty) ? info.maxQty : currentQty + intQuantity;
                }

                if (currentQty > 0) {
                    let currentPointQty = currentItem.getQtyRedeemWithPoints() || 0;
                    let newRedeemWithPoints = info.redeem
                        ? info.qtyRedeemWithPoints > 0 ? info.qtyRedeemWithPoints : intQuantity
                        : 0;
                    let totalPointQty = currentPointQty + newRedeemWithPoints;
                    currentItem.setQtyRedeemWithPoints(totalPointQty);
                    if (currentItem.redeem && (totalPointQty != currentQty && totalPointQty > 0)) {
                        currentItem.setRedeem(true);
                        currentItem.setPartialRedeem(true);
                    }
                    this.setItemQuantity(currentItem.key, parseInt(currentQty), referrer);
                } else {
                    this.removeProduct(currentItem.key);
                }
            }
        } else if (info.qty > 0) {
            const priceType = ShopContext.getPriceType(boolAdr);
            info.product.setPriceAndPvFromType(priceType, info.activeEvents);
            initSalesEventTicket(info.product.getEventPriceTypes());
            const eventPriceTypes = info.product.getEventPriceTypes();
            if (eventPriceTypes.length > 0) {
                SalesEventService.logAnalyticInfo(
                    'event-product-added',
                    info.product.eventName,
                    {sku: info.product.sku, eventPricing: eventPriceTypes.join()}
                );
            }

            currentItem = new CartItem({
                id: info.id,
                product: info.product,
                qty: intQuantity,
                qtyRedeemWithPoints: info.redeem
                    ? info.qtyRedeemWithPoints > 0 ? info.qtyRedeemWithPoints : intQuantity
                    : 0,  //default qtyRedeemWithPoints to the qty if redeem is true
                loyaltyPointsUsed: info.loyaltyPointsUsed,
                maxQty: info.maxQty,
                adr: boolAdr,
                businessPortfolio: boolBp,
                sapLineId: sapLineId,
                canDelete: info.canDelete,
                redeem: info.redeem
            });
            this.addCartItem(currentItem, info.replace, referrer);
        }

        if (currentItem && !currentItem.isSapLineItem() && doneSyncingPersistedCart) {
            NUSAnalytics.setProductDTM(info.product.sku, info.qty, (info.product.getPrice() * info.qty));
            //COM-2043: GA addToCart event
            setCartString(this.getItems({cartItems: true}));
        }
        return currentItem ? currentItem.key : null;
    };

    this.changeProduct = function(itemKey, product) {
        let cartItem = this.getItemByKey(itemKey);

        if (cartItem) {
            const type = cartItem.product.globalProductID === product.globalProductID ? 'other-change' : 'product-changed';
            const removeId = type === 'product-changed' ? cartItem.id : null;
            if (cartItem.isAgelocme()) {
                product.agelocme = cartItem.product.agelocme;
            }
            cartItem.product = product;
            publish(events.shop.CART_UPDATED, {
                modify: {
                    type,
                    removeId,
                    key: itemKey,
                    item: cartItem.toFlatJson()
                }
            });
        }
    };

    /**
     * This, for now, updates the persisted ID into the associated line items.
     *
     * @param pLineInfos
     */
    this.syncPersistedLineInfo = function(pLineInfos = []) {
        pLineInfos.forEach((pLineInfo) => {
            const item = this.getItemByKey(pLineInfo.key);
            if (item) {
                item.id = pLineInfo.id;
            }
        });
    };

    /**
     * replaceBpWithAltBP - a business portfolio can come in different languages.
     *   The user can choose to replace the default BP with one in his language.
     * @memberof Cart
     * @example
     * Cart.replaceBpWithAltBp(altBpSku)
     * @param altBpSku
     */
    this.replaceBpWithAltBp = function(altBpSku) {
        let bpItem = this.getBusinessPortfolio(),
            oldId = bpItem.id,
            oldKey = bpItem.key;

        bpItem.replaceBpWithAltBp(altBpSku);
        bpItem.key = getItemKey(bpItem);
        itemTable.move(oldKey, bpItem.key);
        publish(events.shop.CART_UPDATED, {
            modify: {
                type: 'product-changed',
                key: oldKey,
                removeId: oldId,
                item: bpItem.toFlatJson()
            }
        });
    };

    /**
     * getFirstItemBySku - finds the first regular cart item that has the
     *   sku in the key. There might be more then one item in the cart that
     *   has that sku in the key.
     * @memberof Cart
     * @example
     * Cart.getFirstItemBySku(sku, options)
     * @param sku - required
     * @param options - what item types to search on
     * @returns nuskin.newShop.CartItem
     */
    this.getFirstItemBySku = function(sku, options = {cartItems: true}) {
        let item = this.getItems(options).find(
            (item) => item.product.sku === sku || (item.product.agelocme && item.product.agelocme.code === sku)
        );

        return item ? item.toFlatJson() : null;
    };

    /**
     * Get a item by its' key
     * @memberof Cart
     * @example
     * Cart.getItemByKey(itemKey)
     * @param itemKey
     * @returns {*}
     */
    this.getItemByKey = function(itemKey) {
        return itemTable.get(itemKey);
    };

    /**
     * getItemTitleBySku - searches all items by sku and returns the title.
     * @memberof Cart
     * @example
     * Cart.getItemTitleBySku(sku)
     * @param sku
     * @param options
     * @returns the product's title
     */
    this.getItemTitleBySku = function(sku, options = {cartItems: true}) {
        let item = this.getFirstItemBySku(sku, options);

        return item ? item.title : null;
    };

    /**
     * removeProduct - removes a product from the cart base on the key information
     *   passed in.
     *
     * @param itemKey - the key of the item to remove
     */
    this.removeProduct = function(itemKey) {
        //Set DTM product String
        const item = itemTable.get(itemKey);

        if (item) {
            itemTable.remove(itemKey);
            updateCartTotals();
            publish(events.shop.CART_UPDATED, {remove: {key: itemKey, item: item.toFlatJson()}});

            if (item && !item.isSapLineItem() && doneSyncingPersistedCart) {
                setCartString(this.getItems({cartItems: true}));
                NUSAnalytics.setProductDTM(item.product.sku, item.qty, (item.product.getPrice() * item.qty));
            }
        }
    };

    this.setItemQuantity = function(itemKey, qty, referrer) {
        let item = this.getItemByKey(itemKey),
            updated = false;

        if (item) {
            let qtyDiff = 0;
            if (item.product.hasAvailableQuantity(qty)) {
                qtyDiff = qty - item.qty;
                item.setQty(qty);
                if (AdrService.isAdrOverride()) {
                    if (item.qty !== item.qtyRedeemWithPoints && item.qtyRedeemWithPoints > 0) {
                        item.setPartialRedeem(true);
                    } else {
                        item.setPartialRedeem(false);
                    }
                }
                updateCartTotals();
                updated = true;
            }
            publish(events.shop.CART_UPDATED, {
                modify: {
                    type: 'quantity-changed',
                    key: item.key,
                    item: item.toFlatJson(),
                    qtyDiff: qtyDiff
                }
            }, referrer);
        }
        return updated;
    };

    this.getItemQuantity = function(itemKey) {
        let item = this.getItemByKey(itemKey);

        if (item.qty) {
            return item.qty;
        }
    };

    /**
     * toggleToAdr - sets the ADR status for a product. If the product is already
     *   in the cart with the new status they are combined incrementing the
     *   quantity accordingly.
     *
     * @param fromKey - Key you are coming from, can detect adr status from the key
     * @param activeEvents - list of active sales events for pricing purposes
     * @returns returnItem - The item whose ADR status changed.
     */
    this.toggleToAdr = function(fromKey, activeEvents) {
        let toKey = this.toggleAdrInKey(fromKey),
            isToAdr = isItemKeyAdr(toKey),
            toItem = this.getItemByKey(toKey),
            fromItem = this.getItemByKey(fromKey),
            action;

        if (toItem) {
            toItem.setQty(toItem.getQty() + fromItem.getQty());
            this.removeProduct(fromKey);
            const priceType = ShopContext.getPriceType(isToAdr);
            toItem.getProduct().setPriceAndPvFromType(priceType, activeEvents);
            action = {
                modify: {
                    type: 'quantity-changed',
                    key: toKey,
                    item: toItem.toFlatJson(),
                    qtyDiff: fromItem.getQty()
                }
            };
        } else {
            fromItem.setAdr(isToAdr);
            fromItem.key = toKey;  // should equal getItemKey(fromItem)
            itemTable.move(fromKey, toKey);
            const priceType = ShopContext.getPriceType(isToAdr);
            fromItem.getProduct().setPriceAndPvFromType(priceType, activeEvents);
            action = {
                modify: {
                    type: 'adr-status-changed',
                    key: fromKey,
                    removeId: fromItem.id,
                    item: fromItem.toFlatJson()
                }
            };
        }
        updateCartTotals();
        publish(events.shop.CART_UPDATED, action);
        return isToAdr;
    };

    this.toggleAdrInKey = function(itemKey) {
        let parts = itemKey.split('|');

        parts[1] = parts[1] === 'A' ? 'O' : 'A';

        return parts.join('|');
    };

    this.canToggleAdr = function(itemKey) {
        let item = this.getItemByKey(itemKey);

        return item.canToggleAdr();
    };

    this.containsAdrItems = function() {
        let adrItems = this.getItemCnt({cartAdrItems: true});
        if(adrItems > 0){
            return true;
        }
        return false;
    }

    /**
     * removeItems
     * getItems
     * getItemCnt
     * hasItems
     *
     *  Works with items in the cart based on the flags passed in options. If
     *  more then one flag is passed in then deals with the items in the order
     *  listed below.
     *
     * @param options = {
     *                      all: true,              // removes all line items from the cart.
     *                      cartItems: true,        // removes all cart added items (non-sap).
     *                      cartOrderItems: true,   // removes cart added order items.
     *                      cartAdrItems: true,     // removes cart added ADR items.
     *                      sapItems: true,         // removes all SAP added items.
     *                      sapOrderItems: true,    // removes all SAP order items.
     *                      sapAdrItems: true       // removes all SAP ADR items.
     *                  }
     */
    this.removeItems = function(options) {
        if (options.all) {
            removeAllItems(options);
        } else {
            if (options.cartItems || options.cartOrderItems || options.cartAdrItems) {
                removeCartItems(options);
            }
            if (options.sapItems || options.sapOrderItems || options.sapAdrItems) {
                removeSapItems(options);
            }
        }
    };

    /**
     *
     * @param options = {
     *                      // additional option to the ones listed for removeItems
     *                      cartAgelocMeFilter: {type: 'exclude',  // excludes agelocMe items
     *                                                  'only'      // includes only agelocMe items
     *                                                              // if doesn't exist then includes all
     *                                           includeSku: if 'only' also check for this sku (probably calibration SKU)
     *                                          }
     *                  }
     * @returns {Array}
     */
    this.getItems = function(options = {cartItems: true}){
        let items = [];

        if (options.all) {
            items = getAllItems(options);
        } else {
            if (options.cartItems || options.cartOrderItems || options.cartAdrItems) {
                items = getCartItems(options);
                if (options.cartAgelocMeFilter) {
                    items = applyAgelocMeFilter(items, options.cartAgelocMeFilter);
                }
            }
            if (options.sapItems || options.sapOrderItems || options.sapAdrItems) {
                items = items.concat(getSapItems(options));
            }
        }
        return items;
    };

    this.getItemCnt = function(options = {cartItems: true}) {
        let cnt = 0;

        if (options.all) {
            cnt = countItemQty(getAllItems(options));
        } else {
            if (options.cartItems || options.cartOrderItems || options.cartAdrItems) {
                cnt = countItemQty(getCartItems(options));
            }
            if (options.sapItems || options.sapOrderItems || options.sapAdrItems) {
                cnt = countItemQty(getSapItems(options));
            }
        }
        return cnt;
    };

    this.hasItems = function(options = {cartItems: true}) {
        return this.getItemCnt(options) > 0;
    };

    /**
     * setBusinessPortfolioAdr - based on psv requirements, sets the business portfolio
     *   in the cart to go with the ADR order or the regular order.
     */
    this.setBusinessPortfolioAdr = function(newAdrVal) {
        let bpItem = this.getBusinessPortfolio(),
            changed = false,
            isAdr;
        if (bpItem) {
            isAdr = bpItem.adr;

            if (newAdrVal != null || newAdrVal != undefined) {
                if (newAdrVal != isAdr) {
                    bpItem.setAdr(newAdrVal);
                    changed = true;
                }
            } else {
                if (totalOrderPsv < getCachedConfigField('bpMinOrderPsv') && totalAdrPsv >= getCachedConfigField('bpMinAdrPsv')) {
                    bpItem.setAdr(true);
                    changed = !isAdr;
                } else {
                    bpItem.setAdr(false);
                    changed = isAdr;
                }
            }
        }
        if (changed) {
            publish(events.shop.CART_UPDATED, {
                modify: {
                    type: 'adr-status-changed',
                    key: bpItem.key,
                    removeId: bpItem.id,
                    item: bpItem.toFlatJson()
                }
            });
        }
        return changed;
    };

    /**
     * isBusinessPortfolioAdr - asks if the business portfolio item is ADR or not.
     *
     * @returns {boolean}
     */
    this.isBusinessPortfolioAdr = function() {
        let result = false;
        let bpItem = getBusinessPortfolio();
        if (bpItem) {
            result = bpItem.isAdr();
        }
        return result;
    };

    /**
     * getBusinessPortfolio - returns the business portfolio if there is one.
     *
     * @returns business portfolio item if there is one.
     */
    this.getBusinessPortfolio = function() {
        return getBusinessPortfolio();
    };

    this.addAlternateBusinessPortfolio = function(bpProduct) {
        let bpItem = getBusinessPortfolio();

        if (bpItem) {
            bpItem.addAlternateBusinessPortfolio(bpProduct);
        }
        publish(events.shop.CART_UPDATED, {
            modify: {
                type: 'other-change',
                key: bpItem.key,
                item: bpItem.toFlatJson()
            }
        });
    };

    /**
     * addScanCard - adds a scan card to the cart. Can be passed in as JSON
     *
     * @param _scanCard
     */
    this.addScanCard = function(_scanCard) {
        let scanCard;
        if (_scanCard instanceof ScanCard) {
            scanCard = _scanCard;
        } else {
            scanCard = new ScanCard(_scanCard);
        }
        scanCards.push(scanCard);
    };

    /**
     * removeScanCard - reomves a scan card from the cart.
     *
     * @param _cardNumber - number on scan card to remove.
     */
    this.removeScanCard = function(_cardNumber) {
        let newCardList = [];

        for (let i = 0; i < scanCards.length; i++) {
            if (scanCards[i].cardNumber !== _cardNumber) {
                newCardList.push(scanCards[i]);
            }
        }
        scanCards = newCardList;
    };

    /**
     * hasScanCard - checks to see if the cart has the scan card
     *
     * @param scanCardNumber - scan card number to check for
     * @returns {boolean}
     */
    this.hasScanCard = function(scanCardNumber) {
        let hasScanCardNumber = false;

        for (let i = 0; i < scanCards.length; i++) {
            if (scanCards[i].cardNumber === scanCardNumber) {
                hasScanCardNumber = true;
            }
        }
        return hasScanCardNumber;
    };

    /**
     * trimScanCards - Trims the number of scan cards to the qualified count.
     */
    this.trimScanCards = function() {
        trimScanCards(this.scanQualifiedCnt());
    };

    this.scanQualifiedCnt = function() {
        let cartAdrItems = this.getItems({cartAdrItems: true}),
            adrItem,
            scanQualifiedCount = 0;

        for (let i = 0; i < cartAdrItems.length; i++) {
            adrItem = cartAdrItems[i];
            scanQualifiedCount += adrItem.qty * adrItem.getProduct().scanQualified;
        }

        return scanQualifiedCount;
    };

    /**
     * getOrderSkuQueryParams - Returns query params of ?orderSkus and &adrSkus
     *   if queryParamsExist then start with ? otherwise start with &
     *
     * @param queryParamsExist
     * @returns {string}
     */
    this.getOrderSkuQueryParams = function(queryParamsExist) {
        let queryParams = "",
            items = this.getItems({cartOrderItems: true});
        for (let i=0; i < items.length; i++) {
            if (!queryParamsExist && i === 0) {
                queryParams += "?orderSkus=" + items[i].sku + ":" + items[i].getQty();
            } else {
                queryParams += "&orderSkus=" + items[i].sku + ":" + items[i].getQty();
            }
        }
        items = this.getItems({cartAdrItems: true});
        for (let j=0; j < items.length; j++) {
            queryParams += "&adrSkus=" + items[j].sku + ":" + items[j].getQty();
        }
        return queryParams;
    };

    /**
     * setCartString - Builds the product string for order items, adr items, or
     *   both depending on whether it is an adr order and shipImmediate
     *
     * @param adrOrder
     * @param shipImmediate
     */
    this.setCartString = function(adrOrder, shipImmediate) {
        if (doneSyncingPersistedCart) {
            if (adrOrder === undefined) {
                setCartString(this.getItems({cartItems: true}));
            } else {
                if (!adrOrder) {
                    setCartString(this.getItems({cartOrderItems: true}));
                } else if (shipImmediate) {
                    setCartString(this.getItems({cartItems: true}));
                } else {
                    setCartString(this.getItems({cartAdrItems: true}));
                }
            }
        }
    };

    /**
     * updateLineItemPrices - update the products price if the price type has
     *   changed.
     *
     * @param activeEvents - active sales events for pricing purposes.
     * @returns {boolean}
     */
    this.updateLineItemPrices = function(activeEvents = []) {
        let user = UserService.getUser(),
            items = getCartItems({cartItems: true}),
            changed = false;

        items.forEach(item => {
            const priceType = ShopContext.getPriceType(item.isAdr(), user);
            if (item.getProduct().setPriceAndPvFromType(priceType, activeEvents)) {
                changed = true;
            }
        });

        if (changed) {
            updateCartTotals();
        }

        return changed;
    };

    /**
     * Checks if cart has items tied to a sales event
     * @return {boolean}
     */
    this.hasEventItems = function() {
        let retVal = false;

        getCartItems({cartItems: true}).forEach(item => {
            retVal = retVal || !!item.product.eventName;
        });

        return retVal;
    };

    this.hasPreviewItems = function() {
        return getCartItems({cartOrderItems: true}).some(item => item.product.isExclusive === true);
    };

    this.updateProductEventPrice = function(item, activeEvents) {
        let eventNameAdded = false;
        const user = UserService.getUser();
        const priceType = ShopContext.getPriceType(item.isAdr(), user);
        const eventName = item.product.eventName;
        if (item.product.setPriceAndPvFromType(priceType, activeEvents)) {
            if (!eventName && item.product.eventName) eventNameAdded = true;
            SalesEventService.logAnalyticInfo(
                'product-price-to-event-price',
                item.product.eventName,
                {sku: item.product.sku});
            updateCartTotals();
            publish(events.shop.CART_UPDATED, {
                modify: {
                    type: 'other-change',
                    key: item.key,
                    item: item.toFlatJson()
                }
            });
        }
        return eventNameAdded;
    };

    this.updateEventPrices = function(activeEvents) {
        let eventNameAdded = false;
        getCartItems({cartItems: true}).forEach(item => {
            if (this.updateProductEventPrice(item, activeEvents))
                eventNameAdded = true;
        });
        return eventNameAdded;
    };

    this.setMissingEventNames = async (order) => {
        let cartUpdated = false;
        const orderItems = this.getItems({cartOrderItems: true});
        let nameSet = new Set();
        let itemsChecked = [];

        // Get existing eventNames on any item
        const itemEventNames = orderItems
            .map((item) => item.product.eventName ? item.product.eventName : null)
            .filter((item) => item !== null);

        orderItems.forEach(item => {
            // get events this product has pricing for
            const itemEvents = item.product.getEventPriceTypes();

            if (!item.product.eventName) {
                itemEventNames.some((ien) => {
                    if (itemEvents.includes(ien)) {
                        // set this items eventName if another item already has it set.
                        item.product.eventName = ien;
                        cartUpdated = true;
                    }
                    return !!item.product.eventName; // causes loop to stop if true
                });
                if (!item.product.eventName) {
                    // If eventName is still not set, will need to make service call
                    // to check event status.
                    itemEvents.forEach((ie) => nameSet.add(ie));
                    itemsChecked.push(item);
                }
            }
        });
        const eventNames = [...nameSet];

        if (eventNames.length > 0) {
            // make service call to get status of events on items
            const eventStatuses = await SalesEventService.getEventStatus(eventNames, order.externalId);
            itemsChecked.forEach((item) => {
                const itemEvents = item.product.getEventPriceTypes();
                itemEvents.some((ie) => {
                    if (eventStatuses[ie]) {
                        // set the status if event is active.
                        cartUpdated = true;
                        item.product.eventName == ie;
                        SalesEventService.logAnalyticInfo(
                            'cart-eventname-fixed', ie,
                            {externalId: order.externalId, sku: item.product.sku}
                        )
                    }
                    return !!item.product.eventName; // causes loop to stop if true
                })
            });
        }
        return cartUpdated;
    }

    this.getCartInfo = function(options) {
        return getCartInfo(options);
    };

    this.updateCartLoyalty = function (cartInfo) {
        Object.assign(this, cartInfo);
        updateCartTotals();
        publish(events.shop.CART_UPDATED, {});
    };


    // ---------------------------------------------
    //
    //        Define all private methods.
    //
    // ---------------------------------------------

    function initSalesEventTicket(eventNames) {
        const nameMap = {};

        eventNames.forEach((name) => {
            nameMap[name] = name;
        });
        Object.keys(nameMap).forEach(eventName => {
            events.publish(events.salesevent.INIT_EVENT_TICKET, eventName);
        });
    }

    // Clears the whole cart
    function removeAllItems(options) {
        itemTable.clear();
        trimScanCards(0);
        updateCartTotals();
        publish(events.shop.CART_UPDATED, {removeAll: true, removeCart: options.removeCart === true});
    }

    /**
     * Removes regular cart items based on the flags sent in.
     * @param options
     */
    function removeCartItems(options) {
        const updateInfo = {removeCart: options.removeCart === true};
        const items = getCartItems(options);

        updateInfo.removeItems = {
            items: items.map((item) => item.toFlatJson())
        };

        removeItemsFromItemTable(items);
        if (options.cartItems || options.cartAdrItems) {
            trimScanCards(0);
        }
        updateSapTotals();
        publish(events.shop.CART_UPDATED, updateInfo);
    }

    /**
     * Removes SAP added items base on flags in options
     * @param options
     */
    function removeSapItems(options) {
        let items = getSapItems(options);

        removeItemsFromItemTable(items);
        updateSapTotals();
    }

    // Given a list of CartItems filters out adr/order based on flag
    function filterItemList(items, isAdr) {
        let retItems = [];

        for (let i = 0; i < items.length; i++) {
            if (items[i].isAdr() === isAdr) {
                retItems.push(items[i]);
            }
        }
        return retItems;
    }

    function applyAgelocMeFilter(items, agelocmeFilter) {
        let retItems = [],
            onlyAgelocMe = agelocmeFilter.type === 'only',
            excludeAgelocMe = agelocmeFilter.type === 'exclude',
            includeSku = agelocmeFilter.includeSku;

        for (let i = 0; i < items.length; i++) {
            let itemIsAgelocMe = items[i].isAgelocme();
            if ((includeSku && includeSku === items[i].sku) || (onlyAgelocMe && itemIsAgelocMe) || (excludeAgelocMe && !itemIsAgelocMe)) {
                retItems.push(items[i]);
            }
        }
        return retItems;
    }

    function removeItemsFromItemTable(items) {
        for (let i = 0; i < items.length; i++) {
            itemTable.remove(items[i].key);
        }
    }

    function getCartItems(options) {
        let items = [];

        if (options.cartItems || (options.cartOrderItems && options.cartAdrItems)) {
            items = getAllCartItems();
        } else if (options.cartOrderItems || options.cartAdrItems) {
            items = filterItemList(getAllCartItems(items), options.cartOrderItems ? false : true);
        }
        if (options.cartAgelocMeFilter) {
            items = applyAgelocMeFilter(items, options.cartAgelocMeFilter);
        }
        return items;
    }

    function getSapItems(options) {
        let items = [];

        if (options.sapItems || (options.sapOrderItems && options.sapAdrItems)) {
            items = getAllSapItems();
        } else if (options.sapOrderItems || options.sapAdrItems) {
            items = filterItemList(getAllSapItems(), options.sapOrderItems ? false : true);
        }
        return items;
    }

    function countItemQty(items) {
        let total = 0;
        for (let i=0; i < items.length;i++) {
            total += items[i].getQty();
        }
        return total;
    }

    function getSplitAmounts(items) {
        let item,
            retVal = {
                adrPrice: 0.0,
                adrCsv: 0.0,
                adrPsv: 0.0,
                orderPrice: 0.0,
                orderCsv: 0.0,
                orderPsv: 0.0,
                orderPoints: 0.0,
                loyaltyTotalEarnPrice: 0.0,
                loyaltyTotalRedeemPrice: 0.0,
                loyaltyTotalEarnAndRedeemPrice: 0.0,
                loyaltyTotalAdrEarnAndRedeemPrice: 0.0,
                loyaltyTotalRedeemPriceOrderItems: 0.0
            },
            loyaltyEnabled = getCachedConfigField('enabled'),
            loyaltyInfo;

        if (UserService.isLoggedIn() && loyaltyEnabled) {
            loyaltyInfo = webLoyalty.getLoyaltyRewardsPoints();
        }

        for (let i = 0; i < items.length; i++) {
            item = items[i];
            let totalPrice = item.getTotalPrice()
            if (item.isSapLineItem()) {
                totalPrice /= item.getQty()
            }

            if (item.isAdr()) {
                retVal.adrPrice += totalPrice;
                retVal.adrCsv += item.getTotalCsv();
                retVal.adrPsv += item.getTotalPsv();
            } else {
                retVal.orderPrice += totalPrice;
                retVal.orderCsv += item.getTotalCsv();
                retVal.orderPsv += item.getTotalPsv();
                if (item.isRedeem()) {
                    retVal.orderPoints += item.getPoints();
                    retVal.orderPoints -= UserService.getUser().availablePoints <= retVal.orderPoints ? item.getProduct().points : 0;
                    if (!item.isPartialRedeem()) {
                        retVal.orderPrice -= totalPrice;
                        retVal.orderPsv -= item.getTotalPsv();
                    }
                }
            }

            if (UserService.isLoggedIn()) {

                if (loyaltyEnabled && loyaltyInfo.optInStatus) {
                    let marketAttributes = item.getMarketAttributes();
                    if (marketAttributes && Object.keys(marketAttributes).length !== 0) {
                        if (marketAttributes.earn) {
                            retVal.loyaltyTotalEarnPrice += totalPrice;
                        }
                        if (marketAttributes.redeem) {
                            retVal.loyaltyTotalRedeemPrice += totalPrice;
                            if (!item.isAdr()) {
                                retVal.loyaltyTotalRedeemPriceOrderItems += totalPrice;
                            }
                        }
                        if (marketAttributes.earn && marketAttributes.redeem) {
                            if (!item.isAdr()) {
                                retVal.loyaltyTotalEarnAndRedeemPrice += totalPrice;
                            } else {
                                retVal.loyaltyTotalAdrEarnAndRedeemPrice += totalPrice;
                            }
                        }
                    } else {
                        retVal.loyaltyTotalEarnPrice += totalPrice;
                        retVal.loyaltyTotalRedeemPrice += totalPrice;
                        if (!item.isAdr()) {
                            retVal.loyaltyTotalRedeemPriceOrderItems += totalPrice;
                            retVal.loyaltyTotalEarnAndRedeemPrice += totalPrice;
                        } else {
                            retVal.loyaltyTotalAdrEarnAndRedeemPrice += totalPrice;
                        }
                    }
                }
            }

        }
        return retVal;
    }

    // Non-SAP items
    function getAllCartItems() {
        let lineItems = itemTable.getLineItems();
        let retItems = [];

        for (let i = 0; i < lineItems.length; i++) {
            if (!lineItems[i].isSapLineItem()) {
                retItems.push(lineItems[i]);
            }
        }

        return retItems;
    }

    function getAllSapItems() {
        let lineItems = itemTable.getLineItems();
        let retItems = [];

        for (let i = 0; i < lineItems.length; i++) {
            if (lineItems[i].isSapLineItem()) {
                retItems.push(lineItems[i]);
            }
        }

        return retItems;
    }

    function getAllItems(options) {
        let lineItems = itemTable.getLineItems();
        let retItems = [];

        for (let i = 0; i < lineItems.length; i++) {
            retItems.push(lineItems[i]);
        }

        if (options.cartAgelocMeFilter) {
            retItems = applyAgelocMeFilter(retItems, options.cartAgelocMeFilter);
        }

        return retItems;
    }

    function getItemKey(part1, part2, part3, part4, part5) {
        let key = '',
            sku = part1,
            isAdr = part2,
            isBusinessPortfolio = part3,
            sapLineId = part4,
            extraKeyPart = part5;

        if (part1 instanceof CartItem) {
            const product = part1.getProduct();
            const agelocme = product.agelocme;
            sku = agelocme ? agelocme.code : product.sku;
            isAdr = part1.isAdr();
            isBusinessPortfolio = part1.isBusinessPortfolio();
            sapLineId = part1.getSapLineId();
            extraKeyPart = part1.product.getAgelocmeKeyPart();
        }
        key += sku;
        key += isAdr === true ? '|A' : '|O';
        key += isBusinessPortfolio === true ? '|B' : '|N';
        key += sapLineId ? '|' + sapLineId : '|';
        key += extraKeyPart ? '|' + extraKeyPart : '|';
        return key;
    }

    function isItemKeyAdr(itemKey) {
        return itemKey.split('|')[1] === 'A';
    }

    function trimScanCards(trimToCnt) {
        let newScanCards = scanCards,
            totalCards = scanCards.length;

        if (trimToCnt < totalCards) {
            newScanCards = scanCards.slice(0, trimToCnt);
            publish(events.shop.CART_SCAN_CARDS_CHANGED, {type: 'trimmed', scanCards: newScanCards});
        }
        scanCards = newScanCards;
    }

    function getPersonalOfferMultiplier() {
        const personalOffer = PersonalOfferStorageService.getPersonalOffer();
        const user = UserService.getUser()

        const isEligibleForDiscount = !user || (!user.isDistributor() && !user.isPreferredCustomer())
        const discount = personalOffer && personalOffer.discount && personalOffer.discount.multiplier ? personalOffer.discount.multiplier : 1

        return isEligibleForDiscount ? discount : 1
    }

    function updateCartTotals() {
        let values = getSplitAmounts(getCartItems({cartItems: true}));
        totalOrderPrice = parseFloat(values.orderPrice.toFixed(2));
        totalAdrPrice = parseFloat(values.adrPrice.toFixed(2));
        totalOrderPsv = parseFloat(values.orderPsv.toFixed(2));
        totalAdrPsv = parseFloat(values.adrPsv.toFixed(2));
        totalOrderCsv = parseFloat(values.orderCsv.toFixed(2));
        totalAdrCsv = parseFloat(values.adrCsv.toFixed(2));
        totalOrderPoints = parseFloat(values.orderPoints.toFixed(2));
        if (setBusinessPortfolioPrice()) {
            values = getSplitAmounts(getCartItems({cartItems: true}));
            // The only things changed would have been the price
            totalOrderPrice = parseFloat(values.orderPrice.toFixed(2));
            totalAdrPrice = parseFloat(values.adrPrice.toFixed(2));
        }

        // UPDATE THE LOYALTY POINTS
        const personalOfferMultiplier = getPersonalOfferMultiplier()
        const processLoyaltyValue = (value) => {
            return parseFloat(value * personalOfferMultiplier).toFixed(2)
        }

        loyaltyTotalEarnPrice = processLoyaltyValue(values.loyaltyTotalEarnPrice);
        loyaltyTotalRedeemPrice = processLoyaltyValue(values.loyaltyTotalRedeemPrice);
        loyaltyTotalRedeemPriceOrderItems = processLoyaltyValue(values.loyaltyTotalRedeemPriceOrderItems);
        loyaltyTotalEarnAndRedeemPrice = processLoyaltyValue(values.loyaltyTotalEarnAndRedeemPrice);
        loyaltyTotalAdrEarnAndRedeemPrice = processLoyaltyValue(values.loyaltyTotalAdrEarnAndRedeemPrice);
    }

    function updateSapTotals() {
        const values = getSplitAmounts(getSapItems({sapItems: true}));
        totalSapOrderPrice = parseFloat(values.orderPrice.toFixed(2));
        totalSapAdrPrice = parseFloat(values.adrPrice.toFixed(2));
        totalSapOrderPsv = parseFloat(values.orderPsv.toFixed(2));
        totalSapAdrPsv = parseFloat(values.adrPsv.toFixed(2));
        totalSapOrderCsv = parseFloat(values.orderCsv.toFixed(2));
        totalSapAdrCsv = parseFloat(values.adrCsv.toFixed(2));
        loyaltySapTotalEarnPrice = parseFloat(values.loyaltyTotalEarnPrice.toFixed(2));
        loyaltySapTotalRedeemPrice = parseFloat(values.loyaltyTotalRedeemPrice.toFixed(2));
        loyaltySapTotalRedeemPriceOrderItems = parseFloat(values.loyaltyTotalRedeemPriceOrderItems.toFixed(2));
        loyaltyTotalEarnAndRedeemPrice = parseFloat(values.loyaltyTotalEarnAndRedeemPrice);
        loyaltyTotalAdrEarnAndRedeemPrice = parseFloat(values.loyaltyTotalAdrEarnAndRedeemPrice);
    }

    function getBusinessPortfolio() {
        let retItem = null;
        const items = getCartItems({cartItems: true});

        for (let i = 0; i < items.length; i++) {
            if (items[i].isBusinessPortfolio()) {
                retItem = items[i];
            }
        }
        return retItem;
    }

    /**
     * setBusinessPortfolioPrice - sets the business portfolio's price based on
     *   the psv requirements. If PSV requirements are met will be set to 0.0
     *
     * @returns Business Portfolio item
     */
    function setBusinessPortfolioPrice() {
        const bpItem = getBusinessPortfolio();

        if (bpItem) {
            const bpMinOrderPsv = getCachedConfigField('bpMinOrderPsv');
            if (bpMinOrderPsv <= totalOrderPsv ||
                bpMinOrderPsv <= totalAdrPsv) {

                bpItem.getProduct().setPrice(0.0);
            } else {
                const priceType = getCachedConfigField('showWholeSalePricing') && !AccountManager.isLoggedIn()
                    ? PriceType.WWHL
                    : PriceType.WRTL;
                bpItem.getProduct().setPriceAndPvFromType(priceType);
            }
        }
        return bpItem;
    }

    function setCartString(items) {
        let productString = "";
        if (items) {
            for (let i = 0; i < items.length; i++) {
                productString += `;${items[i].product.sku};${items[i].getQty()};${items[i].getProduct().getPrice()},`;
            }
        }
        productString = productString.substr(0, productString.length - 1);
        if (newShop) {
            newShop.orderDetail = newShop.orderDetail || {};
            newShop.orderDetail.cart = productString;
        }
    }

    function addEventCustomPayload(json) {
        const customPayloadMetadata = storage.metadata.CUSTOM_EVENT_PAYLOAD;
        let customPayload = storage.getItem(customPayloadMetadata);
        if (customPayload) {
            if (customPayload.catalogAddToCart) {
                delete customPayload.catalogAddToCart;
                const product = json.ecommerce.add && json.ecommerce.add.products && json.ecommerce.add.products[0];
                if (product) {
                    Object.assign(product, customPayload);
                }
            } else {
                if (customPayload.constructor === {}.constructor) {
                    Object.assign(json, customPayload);
                }
            }
            storage.removeItem(customPayloadMetadata);
        }
        return json
    }

    function publish(event, json, referrer = 'unknown') {
        if (!cartInitializing) {
            if (event === events.shop.CART_UPDATED) {
                json.cartInfo = getCartInfo();
                if (doneSyncingPersistedCart) {
                    publishCartQtyChange(json, referrer);
                }
                json.pcId = id;
                json.pcVersion = version;
                json.pcType = type;
            }
            events.publish(event, json);
        }
    }

    const publishCartQtyChange = (info, referrer) => {
        let json, event;

        if (info.add && !info.add.item.isSapLineItem) {
            event = events.shop.ADD_TO_CART_NEW;
            json = getCartAddQty(info.add.item, info.add.item.qty, referrer);
        } else if (info.modify && !info.modify.item.isSapLineItem) {
            const qtyDiff = info.modify.qtyDiff;
            const item = info.modify.item;

            if (!item.isSapLineItem && typeof(qtyDiff) != 'undefined' && qtyDiff != 0) {
                event = qtyDiff < 0 ? events.shop.REMOVE_FROM_CART_NEW : events.shop.ADD_TO_CART_NEW;
                json = qtyDiff > 0 ?
                    getCartAddQty(item, qtyDiff, referrer) :
                    getCartRemoveQty(item, qtyDiff);
            }
        } else if (info.remove && !info.remove.item.isSapLineItem) {
            event = events.shop.REMOVE_FROM_CART_NEW;
            json = getCartRemoveQty(info.remove.item, info.remove.item.qty);
        }

        if (json) {
            json.ecommerce = Object.assign(json.ecommerce, {currencyCode: currencyCode});
            json = addEventCustomPayload(json);
            publish(event, json);
        }
    };

    const getCartAddQty = (flatItem, diffQty, referrer) => ({
        'ecommerce': {
            'add': {
                'actionField': {
                    'list': referrer
                },
                'products': [Object.assign(getProductInfoForPublish(flatItem, diffQty), {
                    'method': 'cart',
                    'cartType': flatItem.adr ? 'ADR' : 'Standard'
                })]
            }
        }
    });

    const getCartRemoveQty = (flatItem, diffQty) => ({
        'ecommerce': {
            'remove': {
                'products': [getProductInfoForPublish(flatItem, diffQty)]
            }
        }
    });

    const getProductInfoForPublish = (flatItem, diffQty) => ({
        'name': flatItem.title,
        'id': flatItem.sku,
        'price': flatItem.price,
        'quantity': Math.abs(diffQty)
    });

    function getCartInfo(options) {
        return {
            id: id,
            version: version,
            type: type,
            qty: scope.getItemCnt(options),
            totalOrderPrice: totalOrderPrice,
            totalAdrPrice: totalAdrPrice,
            totalOrderPsv: totalOrderPsv,
            totalAdrPsv: totalAdrPsv,
            totalOrderCsv: totalOrderCsv,
            totalAdrCsv: totalAdrCsv,
            totalOrderPoints: totalOrderPoints,
            summerPromo: summerPromo,
            loyaltyRewards: loyaltyRewards,
            loyaltyRewardPoints: loyaltyRewardPoints,
            loyaltyAdrRewards: loyaltyAdrRewards,
            loyaltyAdrRewardPoints: loyaltyAdrRewardPoints,
            loyaltyTotalEarnPrice: loyaltyTotalEarnPrice,
            loyaltyTotalEarnAndRedeemPrice: loyaltyTotalEarnAndRedeemPrice,
            loyaltyTotalAdrEarnAndRedeemPrice: loyaltyTotalAdrEarnAndRedeemPrice,
            loyaltyTotalRedeemPrice: loyaltyTotalRedeemPrice,
            loyaltyTotalRedeemPriceOrderItems: loyaltyTotalRedeemPriceOrderItems,
            loyaltySapTotalEarnPrice: loyaltySapTotalEarnPrice,
            loyaltySapTotalRedeemPrice: loyaltySapTotalRedeemPrice,
            loyaltySapTotalRedeemPriceOrderItems: loyaltySapTotalRedeemPriceOrderItems
        };
    }

    /**
     * ItemTable - where the actual line items are stored. Line items are stored
     *   in both a map and a list.
     *
     * @constructor
     */
    function ItemTable() {
        let table = {
            items: []
        };

        this.getInfo = function() {
            return table;
        };

        this.hasItem = function(key) {
            return !!table[key];
        };

        this.getLineItems = function() {
            return table.items;
        };

        this.add = function(lineItem, replace) {
            if (!this.hasItem(lineItem.key) || replace) {
                if (!this.hasItem(lineItem.key)) {
                    table.items.push(lineItem);
                }
                table[lineItem.key] = lineItem;
            } else {
                console.log(`Tried to add duplicate item to the cart. Key = ${lineItem.key}`);
            }
        };

        this.get = function(key) {
            if (this.hasItem(key)) {
                return table[key];
            }
        };

        this.remove = function(key) {
            const lineItem = this.get(key);

            if (lineItem) {
                table.items.splice(table.items.indexOf(lineItem), 1);
                delete table[key];
            }
        };

        this.move = function(oldKey, newKey) {
            if (this.hasItem(oldKey)) {
                table[newKey] = table[oldKey];
                delete table[oldKey];
            }
        };

        this.clear = function() {
            table = {
                items: []
            };
        };
    }

    // ---------------------------------------------
    //
    //        Cart init code
    //
    // ---------------------------------------------
    if (cartData) {
        let cartItem,
            scanCard,
            i;

        id = cartData.id || '';
        version = cartData.version || -1;

        if (cartData.products) {
            for (i = 0; i < cartData.products.length; i++) {
                cartItem = new CartItem(cartData.products[i]);
                this.addCartItem(cartItem);
            }
        }

        if (cartData.scanCards) {
            for (i = 0; i < cartData.scanCards.length; i++) {
                scanCard = new ScanCard(cartData.scanCards[i]);
                this.addScanCard(scanCard);
            }
        }

        if (cartData.timestamp) {
            timestamp = cartData.timestamp;
        }
        if (cartData.cntryCd) {
            cntryCd = cartData.cntryCd;
            currencyCode = CurrencyService.getCurrency(cntryCd).currencyCode;
        }
        if (cartData.type) {
            type = cartData.type;
        }
        if (cartData.userId) {
            userId = cartData.userId;
        }
        if (cartData.summerPromo) {
            summerPromo = cartData.summerPromo;
        }
        if (cartData.payForShippingWithPoints) {
            payForShippingWithPoints = cartData.payForShippingWithPoints;
        }
        if (typeof cartData.signupInfo == 'object') {
            signupInfo = cartData.signupInfo;
        }
        if (cartData.isPitchCart) {
            isPitchCart = cartData.isPitchCart;
        }
        if (cartData.isGift) {
            isGift = cartData.isGift;
        }

        if (cartData.loyaltyRewards) {
            loyaltyRewards = cartData.loyaltyRewards;
        }

        if (cartData.loyaltyRewardPoints) {
            loyaltyRewardPoints = cartData.loyaltyRewardPoints;
        }

        if (cartData.loyaltyAdrRewards) {
            loyaltyAdrRewards = cartData.loyaltyAdrRewards;
        }

        if (cartData.loyaltyAdrRewardPoints) {
            loyaltyAdrRewardPoints = cartData.loyaltyAdrRewardPoints;
        }

        if (cartData.loyaltyTotalEarnPrice) {
            loyaltyTotalEarnPrice = cartData.loyaltyTotalEarnPrice;
        }

        if (cartData.loyaltyTotalEarnAndRedeemPrice) {
            loyaltyTotalEarnAndRedeemPrice = cartData.loyaltyTotalEarnAndRedeemPrice;
        }

        if (cartData.loyaltyTotalAdrEarnAndRedeemPrice) {
            loyaltyTotalAdrEarnAndRedeemPrice = cartData.loyaltyTotalAdrEarnAndRedeemPrice;
        }

        if (cartData.loyaltyTotalRedeemPrice) {
            loyaltyTotalRedeemPrice = cartData.loyaltyTotalRedeemPrice;
        }

        if (cartData.loyaltyTotalRedeemPriceOrderItems) {
            loyaltyTotalRedeemPriceOrderItems = cartData.loyaltyTotalRedeemPriceOrderItems;
        }

        if (cartData.loyaltySapTotalEarnPrice) {
            loyaltySapTotalEarnPrice = cartData.loyaltySapTotalEarnPrice;
        }

        if (cartData.loyaltySapTotalRedeemPrice) {
            loyaltySapTotalRedeemPrice = cartData.loyaltySapTotalRedeemPrice;
        }

        if (cartData.loyaltySapTotalRedeemPriceOrderItems) {
            loyaltySapTotalRedeemPriceOrderItems = cartData.loyaltySapTotalRedeemPriceOrderItems;
        }

        cartInitializing = false;
    }


    // ---------------------------------------------
    //
    //        Public Getter / Setter Methods
    //
    // ---------------------------------------------
    Object.defineProperties(this, {
        id: {
            get: function() {
                return id || '';
            },
            set: function(_id) {
                id = _id;
            }
        },
        version: {
            get: function() {
                return version;
            },
            set: function(_version) {
                version = _version;
            }
        },
        type: {
            get: function() {
                return type;
            },
            set: function(_type) {
                type = _type;
            }
        },
        payForShippingWithPoints: {
            get: function() {
                return payForShippingWithPoints;
            },
            set: function(_payForShippingWithPoints) {
                payForShippingWithPoints = _payForShippingWithPoints;
            }
        },
        inSignup: {
            get: function() {
                return signupInfo.inSignup;
            },
            set: function(_inSignup) {
                signupInfo.inSignup = _inSignup;
            }
        },
        checkForSignup: {
            get: function() {
                return signupInfo.checkForSignup;
            },
            set: function(_checkForSignup) {
                signupInfo.checkForSignup = _checkForSignup;
            }
        },
        timestamp: {
            get: function() {
                return timestamp;
            },
            set: function(_timestamp) {
                timestamp = _timestamp;
            }
        },
        summerPromo: {
            get: function() {
                return summerPromo;
            },
            set: function(_summerPromo) {
                summerPromo = _summerPromo;
            }
        },
        isPitchCart: {
            get: function() {
                return isPitchCart;
            },
            set: function(_isPitchCart) {
                isPitchCart = _isPitchCart;
            }
        },
        isGift: {
            get: function() {
                return isGift;
            },
            set: function(_isGift) {
                if (_isGift === true) {
                    isGift = _isGift;
                }
            }
        },
        userId: {
            get: function() {
                return userId;
            },
            set: function(_userId) {
                userId = _userId;
            }
        },
        cntryCd: {
            get: function() {
                return cntryCd;
            }
        },
        scanCards: {
            get: function() {
                return scanCards;
            }
        },
        totalOrderPrice: {
            get: function() {
                return totalOrderPrice;
            }
        },
        totalOrderPoints: {
            get: function() {
                return totalOrderPoints;
            }
        },
        totalAdrPrice: {
            get: function() {
                return totalAdrPrice;
            }
        },
        totalOrderPsv: {
            get: function() {
                return totalOrderPsv;
            }
        },
        totalAdrPsv: {
            get: function() {
                return totalAdrPsv;
            }
        },
        totalOrderCsv: {
            get: function() {
                return totalOrderCsv;
            }
        },
        totalAdrCsv: {
            get: function() {
                return totalAdrCsv;
            }
        },
        totalSapOrderPrice: {
            get: function() {
                return totalSapOrderPrice;
            }
        },
        totalSapAdrPrice: {
            get: function() {
                return totalSapAdrPrice;
            }
        },
        totalSapOrderPsv: {
            get: function() {
                return totalSapOrderPsv;
            }
        },
        totalSapAdrPsv: {
            get: function() {
                return totalSapAdrPsv;
            }
        },
        totalSapOrderCsv: {
            get: function() {
                return totalSapOrderCsv;
            }
        },
        totalSapAdrCsv: {
            get: function() {
                return totalSapAdrCsv;
            }
        },
        loyaltyRewards: {
            get: function() {
                return loyaltyRewards;
            },
            set: function (_loyaltyRewards) {
                loyaltyRewards = _loyaltyRewards;
            }
        },
        loyaltyRewardPoints: {
            get: function() {
                return loyaltyRewardPoints;
            },
            set: function(_loyaltyRewardPoints) {
                loyaltyRewardPoints = _loyaltyRewardPoints;
            }
        },
        loyaltyAdrRewards: {
            get: function() {
                return loyaltyAdrRewards;
            },
            set: function (_loyaltyAdrRewards) {
                loyaltyAdrRewards = _loyaltyAdrRewards;
            }
        },
        loyaltyAdrRewardPoints: {
            get: function() {
                return loyaltyAdrRewardPoints;
            },
            set: function(_loyaltyAdrRewardPoints) {
                loyaltyAdrRewardPoints = _loyaltyAdrRewardPoints;
            }
        },
        loyaltyTotalEarnPrice: {
            get: function() {
                return loyaltyTotalEarnPrice;
            },
            set: function(_loyaltyTotalEarnPrice) {
                loyaltyTotalEarnPrice = _loyaltyTotalEarnPrice;
            }
        },
        loyaltyTotalEarnAndRedeemPrice: {
            get: function() {
                return loyaltyTotalEarnAndRedeemPrice;
            },
            set: function(_loyaltyTotalEarnAndRedeemPrice) {
                loyaltyTotalEarnAndRedeemPrice = _loyaltyTotalEarnAndRedeemPrice;
            }
        },
        loyaltyTotalAdrEarnAndRedeemPrice: {
            get: function() {
                return loyaltyTotalAdrEarnAndRedeemPrice;
            },
            set: function(_loyaltyTotalAdrEarnAndRedeemPrice) {
                loyaltyTotalAdrEarnAndRedeemPrice = _loyaltyTotalAdrEarnAndRedeemPrice;
            }
        },
        loyaltyTotalRedeemPrice: {
            get: function() {
                return loyaltyTotalRedeemPrice;
            },
            set: function(_loyaltyTotalRedeemPrice) {
                loyaltyTotalRedeemPrice = _loyaltyTotalRedeemPrice;
            }
        },
        loyaltyTotalRedeemPriceOrderItems: {
            get: function() {
                return loyaltyTotalRedeemPriceOrderItems;
            },
            set: function(_loyaltyTotalRedeemPriceOrderItems) {
                loyaltyTotalRedeemPriceOrderItems = _loyaltyTotalRedeemPriceOrderItems;
            }
        },
        loyaltySapTotalEarnPrice: {
            get: function() {
                return loyaltySapTotalEarnPrice;
            },
            set: function(_loyaltySapTotalEarnPrice) {
                loyaltySapTotalEarnPrice = _loyaltySapTotalEarnPrice;
            }
        },
        loyaltySapTotalRedeemPrice: {
            get: function() {
                return loyaltySapTotalRedeemPrice;
            },
            set: function(_loyaltySapTotalRedeemPrice) {
                loyaltySapTotalRedeemPrice = _loyaltySapTotalRedeemPrice;
            }
        },
        loyaltySapTotalRedeemPriceOrderItems: {
            get: function() {
                return loyaltySapTotalRedeemPriceOrderItems;
            },
            set: function(_loyaltySapTotalRedeemPriceOrderItems) {
                loyaltySapTotalRedeemPriceOrderItems = _loyaltySapTotalRedeemPriceOrderItems;
            }
        }
    });
}
